import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { ICFormikDropDown } from "../../core-component/formik";
import {
  FormRightModalWrapper,
  ICButton,
  ICConfirmationModal,
  Loader,
} from "../../core-component/ui";
import { IOption } from "../../data/AppInterface";
import UseTranslationHook from "../../hooks/UseTranslationHook";
import { BIT_VALUE } from "../../utils/AppEnumerations";
import {
  addCurrencySign,
  dateFormat,
  formatDuration,
  formatToCurrency,
  hasAnyModification,
} from "../../utils/AppFunctions";
import { getFirmNameDdl } from "./FirmPurchasedSubscriptionViewController";
import {
  fetchAssignableSubscriptionPackage,
  fetchFirmSubscriptionDetail,
  FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES,
  firmSubscriptionAssignmentValidationSchema,
  handleSubmitFirmSubscriptionAssignment,
  IAssignableSubscriptionPackage,
  IFirmSubscriptionAssignmentForm,
  IFirmSubscriptionDetail,
} from "./FirmSubscriptionAssignmentFormController";

interface IFirmSubscriptionAssignmentFormProps {
  handleClose: (status: boolean) => void;
}

const FirmSubscriptionAssignmentForm: React.FC<
  IFirmSubscriptionAssignmentFormProps
> = ({ handleClose }) => {
  const { t } = UseTranslationHook();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showDiscardWarning, setShowDiscardWarning] = useState(false);
  const [firmName, setFirmName] = useState<IOption<string>[]>([]);
  const [subscriptionPackageList, setSubscriptionPackageList] = useState<
    IAssignableSubscriptionPackage[]
  >([]);
  const [firmSubscriptionDetail, setFirmSubscriptionDetail] =
    useState<IFirmSubscriptionDetail>();

  useEffect(() => {
    fetchFormData();
  }, []);

  const fetchFormData = async () => {
    setLoading(true);
    await Promise.all([
      getFirmNameDdl(setFirmName, false),
      fetchAssignableSubscriptionPackage(setSubscriptionPackageList),
    ]);
    setLoading(false);
  };

  const subscriptionPackageOptions: IOption<string>[] =
    subscriptionPackageList.map((subscriptionPackage) => ({
      display_value: subscriptionPackage.name,
      data_value: subscriptionPackage.id,
    }));

  const detailRow = (label: string, value: string) => (
    <div className="flex items-center mt-2" key={label}>
      <div className="text-sm text-muted">{label} :</div>
      <div className="ml-2">{value}</div>
    </div>
  );

  const currentSubscriptionView = () => {
    if (!detailLoading && !firmSubscriptionDetail) {
      return null;
    }

    const subscription = firmSubscriptionDetail?.subscription;

    return (
      <div className="bg-base rounded-md p-4">
        <div className="font-semibold">{t("latest_subscription")}</div>
        {detailLoading ? (
          <div className="text-sm text-muted mt-2">{t("loading")}</div>
        ) : subscription ? (
          <>
            {detailRow(t("package"), subscription.subscription_package_name)}
            {detailRow(t("expiry_date"), dateFormat(subscription.end_date))}
            {detailRow(
              t("expiry_days"),
              subscription.is_expired === BIT_VALUE.One
                ? t("subscription_expired")
                : String(subscription.remaining_days)
            )}
          </>
        ) : (
          <div className="text-sm text-muted mt-2">
            {t("no_subscription_found")}
          </div>
        )}
      </div>
    );
  };

  const priceCell = (label: string, amount: number) => (
    <div key={label}>
      <div className="text-xs text-muted">{label}</div>
      <div className="mt-1">
        {addCurrencySign(formatToCurrency(amount))}
      </div>
    </div>
  );

  const selectedPackageView = (subscriptionPackageId: string) => {
    const selectedPackage = subscriptionPackageList.find(
      (subscriptionPackage) => subscriptionPackage.id === subscriptionPackageId
    );

    if (!selectedPackage) {
      return null;
    }

    return (
      <div className="bg-base rounded-md p-4">
        <div className="font-semibold">{t("package_detail")}</div>
        <div className="flex items-end justify-between mt-3">
          <div>
            <div className="text-xs text-muted">{t("total")}</div>
            <div className="text-2xl font-semibold mt-1">
              {addCurrencySign(formatToCurrency(selectedPackage.total))}
            </div>
          </div>
          <div className="text-sm text-muted">
            {formatDuration(selectedPackage.validity)}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-3 border-t mt-3 pt-3">
          {priceCell(t("base_price"), selectedPackage.base_price)}
          {priceCell(t("sgst"), selectedPackage.sgst)}
          {priceCell(t("cgst"), selectedPackage.cgst)}
          {priceCell(t("igst"), selectedPackage.igst)}
        </div>
      </div>
    );
  };

  const checkFormModification = (values: IFirmSubscriptionAssignmentForm) => {
    if (hasAnyModification(values, FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES)) {
      setShowDiscardWarning(true);
    } else {
      handleClose(false);
    }
  };

  return (
    <>
      {loading ? (
        <Loader />
      ) : (
        <>
          <Formik
            initialValues={FIRM_SUBSCRIPTION_ASSIGNMENT_INITIAL_VALUES}
            validationSchema={firmSubscriptionAssignmentValidationSchema(t)}
            onSubmit={(values) =>
              handleSubmitFirmSubscriptionAssignment(
                values,
                setSubmitLoading,
                handleClose
              )
            }
          >
            {(formikProps) => {
              const { handleSubmit, values } = formikProps;

              return (
                <FormRightModalWrapper
                  containerClassName="!w-1/3"
                  onClose={() => checkFormModification(values)}
                  title={t("assign_subscription")}
                >
                  <Form onSubmit={handleSubmit} className="flex flex-col h-full">
                    <div className="flex-1 space-y-4">
                      <ICFormikDropDown
                        {...formikProps}
                        name="firm_id"
                        placeholder={t("firm_name")}
                        options={firmName}
                        searchable
                        required
                        onChange={(firmId: string) =>
                          fetchFirmSubscriptionDetail(
                            firmId,
                            setFirmSubscriptionDetail,
                            setDetailLoading
                          )
                        }
                      />
                      {currentSubscriptionView()}
                      <ICFormikDropDown
                        {...formikProps}
                        name="subscription_package_id"
                        placeholder={t("subscription_package")}
                        options={subscriptionPackageOptions}
                        searchable
                        required
                      />
                      {selectedPackageView(values.subscription_package_id)}
                    </div>
                    <ICButton
                      loading={submitLoading}
                      className={"w-full"}
                      children={t("submit")}
                    />
                  </Form>
                </FormRightModalWrapper>
              );
            }}
          </Formik>
          {showDiscardWarning && (
            <ICConfirmationModal
              title={t("discard_warning")}
              message={t("discard_unsaved_changes")}
              onClose={(value) => {
                setShowDiscardWarning(false);
                value && handleClose(false);
              }}
            />
          )}
        </>
      )}
    </>
  );
};

export default FirmSubscriptionAssignmentForm;
